/**
 * Pure decision helper for drag-drop reschedule routing in
 * `SchedulingPage`. Extracted so the routing rules (missing-context error,
 * duration-preserving end computation) can be exercised by unit tests
 * without rendering the whole page + every service hook.
 *
 * Drag-drop reschedules the SINGLE occurrence — recurring or not (Flutter
 * parity: `QuickRescheduleAppointmentEvent` → PUT /Appointment/Reschedule
 * with just {id,start,end}; no scope prompt on drag). Series-wide changes
 * live in the recurring-series edit menu, not the drag gesture.
 *
 * The page-level handler dispatches on the returned `kind`:
 *   - `error`   — the source row is missing essential fields; toast + skip.
 *   - `this-only` — fire the single-occurrence Reschedule with the computed
 *                 start / end pair.
 */

import type { AppointmentsDetailsResponse } from '@/types/scheduling-api';

export interface DropInput {
  id: string;
  newStartISO: string;
}

export type DropRoute =
  | { kind: 'error'; message: string }
  | {
      kind: 'this-only';
      payload: { id: string; newStartISO: string; newEndISO: string };
    };

export interface RouteDropParams {
  /** Cached day-appointments list — used to look up the source row. */
  dayAppointments: AppointmentsDetailsResponse | undefined;
  /** The drop event from WeekCalendar. */
  event: DropInput;
}

export function routeAppointmentDrop(params: RouteDropParams): DropRoute {
  const { dayAppointments, event } = params;

  const apt = dayAppointments?.appointments?.find((a) => a.id === event.id);
  if (!apt?.start || !apt?.end) {
    return { kind: 'error', message: 'Cannot reschedule — missing source times.' };
  }

  const durationMs = new Date(apt.end).getTime() - new Date(apt.start).getTime();
  const newEnd = new Date(new Date(event.newStartISO).getTime() + durationMs);
  const newEndISO = newEnd.toISOString();

  return {
    kind: 'this-only',
    payload: { id: event.id, newStartISO: event.newStartISO, newEndISO },
  };
}
